import React from 'react';
import Icon from '../../shared/Icon';
import './AlertSteps.css';
import ReportStepSchedule from './ReportStepSchedule';
import AlertGuardFields from './AlertGuardFields';
import AlertReviewCard from './AlertReviewCard';
import AlertMatchCount from './AlertMatchCount';
import AlertSpecProblems from './AlertSpecProblems';
import ReviewRequiredBanner from '../../shared/ReviewRequiredBanner';
import { useReviewRequiredDatasets } from '../useReviewRequiredDatasets';

/**
 * Step 4 — Schedule and review.
 *
 * The Reports wizard spends two steps on Schedule and Review; the alert spine
 * is still four steps because step 1 is the template picker, so these merge.
 * The frequency select and time picker are the reused Report schedule step, so
 * they cannot drift.
 *
 * "Create inactive" is not a control here — there is deliberately no active
 * toggle in this wizard, because `AutomationCreate` has no `alertActive` field.
 * Activation is a separate action with its own audit entry, so an author cannot
 * switch an alert on through a create form even by accident.
 */
const AlertStepSchedule = ({ formData, onSetFormData, dryRun, problems = [], notReadyReason }) => {
  // PRD-1359: same warning as step 2, repeated under the Review title. The
  // alert wizard merges Schedule and Review into this one step, so "under the
  // Review title" is the heading below, not the top of the step.
  const reviewDatasets = useReviewRequiredDatasets(formData.reportDatasetIds);

  return (
  <div className="alert-step-content">
    <ReportStepSchedule formData={formData} onSetFormData={onSetFormData} />

    <AlertGuardFields formData={formData} onSetFormData={onSetFormData} />

    <AlertMatchCount
      result={dryRun?.result}
      error={dryRun?.error}
      loading={dryRun?.loading}
      ready={dryRun?.ready}
      notReadyReason={notReadyReason}
    />

    <h4 className="alert-threshold-heading">Review</h4>
    <ReviewRequiredBanner
      datasets={reviewDatasets}
      variant="automation"
      className="alert-inline-block"
    />
    <AlertReviewCard formData={formData} />

    <AlertSpecProblems problems={problems} heading="Cannot create yet" />

    <div className="resui-field">
      <label className="resui-field-label" htmlFor="alert-name">
        Alert name <span className="resui-field-required">*</span>
      </label>
      <input
        id="alert-name"
        type="text"
        className="resui-input"
        placeholder="Enter alert name"
        value={formData.automationName ?? ''}
        onChange={(e) => onSetFormData((prev) => ({ ...prev, automationName: e.target.value }))}
      />
    </div>

    <div className="resui-field">
      <label className="resui-field-label" htmlFor="alert-description">
        Description <span className="resui-field-optional">(optional)</span>
      </label>
      <textarea
        id="alert-description"
        className="resui-textarea"
        placeholder="Describe what this alert watches for"
        rows={3}
        value={formData.description ?? ''}
        onChange={(e) => onSetFormData((prev) => ({ ...prev, description: e.target.value }))}
      />
    </div>

    <div className="resui-banner resui-banner--info alert-inline-block">
      <Icon name="toggle_off" />
      <span className="resui-banner-text">
        This alert will be created inactive. Activate it from the alert card
        when you are satisfied with the dry run.
      </span>
    </div>
  </div>
  );
};

export default AlertStepSchedule;
